'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

import ViverseButton from '@/components/ViverseButton'
import ViverseCheckbox from '@/components/ViverseCheckbox'
import ViverseDialog from '@/components/ViverseDialog'
import ViverseInput from '@/components/ViverseInput'
import ViverseTextarea from '@/components/ViverseTextarea'
import IconCopy from '@/components/assets/icon_copy.svg'
import { IFRAME_SEARCH_PARAM_KEYS } from '@/constants/iframe'
import { createEmbedCode } from '@/lib/embed'
import { showToast } from '@/lib/toast'
import { Room } from '@/services/cms'

type EmbedDialogProps = {
  title: string
  isDialogOpen: boolean
  info: Partial<Room>
  is3DCommerce?: boolean
  handleOpenChange: (open: boolean) => void
  handleClose: () => void
}

const BASE_EMBED_QUERY_PARAMS = [
  IFRAME_SEARCH_PARAM_KEYS.FULL3D,
  IFRAME_SEARCH_PARAM_KEYS.NO_COOKIE,
]
const EMBED_QUERY_PARAMS_WITH_LOADING = [IFRAME_SEARCH_PARAM_KEYS.IS_3D_COMMERCE]
const EMBED_QUERY_PARAMS_WITH_VIEW = [IFRAME_SEARCH_PARAM_KEYS.VIEW]

function getEmbedQueryParams(options: { customLoadingEnabled: boolean; is3DCommerce?: boolean }) {
  const { customLoadingEnabled = false, is3DCommerce = false } = options

  return [
    ...BASE_EMBED_QUERY_PARAMS,
    ...(customLoadingEnabled ? EMBED_QUERY_PARAMS_WITH_LOADING : []),
    ...(is3DCommerce ? EMBED_QUERY_PARAMS_WITH_VIEW : []),
  ]
}

function EmbedDialog({
  title,
  info,
  isDialogOpen,
  is3DCommerce,
  handleOpenChange,
  handleClose,
}: EmbedDialogProps) {
  const t = useTranslations()

  const textInputRef = useRef<HTMLInputElement>(null)

  const [textAreaInputValue, setTextAreaInputValue] = useState('')
  const [customLoadingEnabled, setCustomLoadingEnabled] = useState(false)

  useEffect(() => {
    if (isDialogOpen) {
      setTextAreaInputValue(
        createEmbedCode(info, getEmbedQueryParams({ customLoadingEnabled, is3DCommerce })),
      )
    }
  }, [customLoadingEnabled, info, isDialogOpen, is3DCommerce])

  const handleEmbedCodeCopy = () => {
    navigator.clipboard.writeText(textAreaInputValue)

    showToast({
      message: t('embed-copied-toast.title'),
      type: 'success',
    })
    setCustomLoadingEnabled(false)
    handleClose()
  }

  const handleCustomLoadingToggle = () => {
    setCustomLoadingEnabled((prev) => {
      const newValue = !prev
      setTextAreaInputValue(
        createEmbedCode(info, [
          ...getEmbedQueryParams({
            customLoadingEnabled: newValue,
            is3DCommerce,
          }),
        ]),
      )
      return newValue
    })
  }

  const handleTextInputChange = () => {
    const newEmbedCode = createEmbedCode(info, [
      ...getEmbedQueryParams({ customLoadingEnabled }),
      { name: 'dl', value: textInputRef.current?.value },
      { name: 'd', value: textInputRef.current?.value },
      { name: 'mp', value: textInputRef.current?.value },
      { name: 'ml', value: textInputRef.current?.value },
    ])
    setTextAreaInputValue(newEmbedCode)
  }

  return (
    <ViverseDialog
      contentClassName="flex flex-col min-h-[216px] w-full md:max-w-[640px]"
      triggerClassName="contents"
      titleClassName="border-0 md:text-[24px]"
      title={title}
      open={isDialogOpen}
      onClose={handleClose}
      onOpenChange={handleOpenChange}
    >
      <div className="flex w-full flex-1 flex-col gap-8 px-10 pb-8">
        <div className="flex flex-col gap-2">
          <ViverseTextarea
            className="bg-vive-primary-70 max-h-[340px] w-full border-transparent text-base"
            showMaxLength={false}
            value={textAreaInputValue}
            onChange={(e) => setTextAreaInputValue(e.target.value)}
          />

          <ViverseCheckbox
            id="embed-checkbox"
            checked={customLoadingEnabled}
            onCheckedChange={handleCustomLoadingToggle}
          >
            {t('embed-modal.checkbox-label')}
          </ViverseCheckbox>

          {customLoadingEnabled && (
            <ViverseInput
              ref={textInputRef}
              className="bg-vive-primary-70 border-transparent text-base"
              onChange={handleTextInputChange}
              placeholder={t('embed-modal.input-placeholder')}
            />
          )}
        </div>

        <ViverseButton
          className="m-auto md:min-h-[48px] md:text-[18px]"
          onClick={handleEmbedCodeCopy}
        >
          <IconCopy className="mr-2 size-6" />
          {t('embed-modal.button')}
        </ViverseButton>
      </div>
    </ViverseDialog>
  )
}

export default EmbedDialog
